import { eedApi } from '@/api/eedApi';
import { emsApi } from '@/api/emsApi';

export type EedCallCapabilities = {
  phoneNumber: string | null;
  hasMediaUploadCapability: boolean;
  hasVideoCapability: boolean;
  hasAdditionalData: boolean;
};

export function eedCallCapabilitiesQueryKey(
  incidentId: string,
  phoneNumbers: readonly string[],
): readonly unknown[] {
  return ['incident', incidentId, 'eed-call-capabilities', ...phoneNumbers];
}

export async function fetchEedCallCapabilities(phoneNumbers: readonly string[]): Promise<EedCallCapabilities[]> {
  if (phoneNumbers.length === 0) {
    return [];
  }

  try {
    const { data } = await eedApi.GET('/v1/get-call-capabilities', {
      params: { query: { Numbers: [...phoneNumbers] } },
    });

    return (data?.capabilities ?? []).map((capability) => ({
      phoneNumber: capability.phoneNumber ?? null,
      hasMediaUploadCapability: capability.hasMediaUploadCapability ?? false,
      hasVideoCapability: capability.hasVideoCapability ?? false,
      hasAdditionalData: capability.hasAdditionalData ?? false,
    }));
  } catch (error) {
    console.log(`[IncidentDetail] EED capability check failed: ${String(error)}`);
    return [];
  }
}

export type EedEmergencyContact = {
  name?: string | null;
  tel?: string | null;
  tel_label?: string | null;
  relationship?: string | null;
};

export type EedCallInformation = {
  medium?: string | null;
  trigger_type?: string | null;
  device_language?: string | null;
  tel?: string | null;
  tel_alt?: string | null;
};

export type EedCallerInformation = {
  name?: string | null;
  age?: number | null;
  height?: number | null;
  weight?: number | null;
  blood_type?: string | null;
  medical_conditions?: string | null;
  medical_notes?: string | null;
  allergies?: string | null;
  medications?: string | null;
  gestational_age?: string | null;
  emergency_contacts?: EedEmergencyContact[] | null;
};

export type EedAdditionalDataResponse = {
  additionalData?: {
    call_information?: EedCallInformation | null;
    caller_information?: EedCallerInformation | null;
  } | null;
  batteryLevel?: string | null;
};

export async function fetchEedAdditionalData(phoneNumber: string): Promise<EedAdditionalDataResponse | null> {
  const { data } = await eedApi.GET('/v1/get-additional-data/{callId}', {
    params: { path: { callId: phoneNumber } },
  });

  return (data as EedAdditionalDataResponse | undefined) ?? null;
}

export async function requestEedMedia(incidentId: string, phoneNumber: string): Promise<void> {
  const { response } = await eedApi.POST('/v1/send-psap-request/media', {
    body: { incidentId, callId: phoneNumber },
  });

  if (!response.ok) {
    throw new Error(`Failed to request EED media (HTTP ${response.status})`);
  }
}

export async function requestEedVideo(callId: string, videoId: string): Promise<void> {
  const { response } = await eedApi.POST('/v1/send-psap-request/video', {
    body: { callId, videoId },
  });

  if (!response.ok) {
    throw new Error(`Failed to resend EED video request (HTTP ${response.status})`);
  }
}

export type PsapInfo = {
  id: string;
  emergencyServiceType: number | string;
  displayName: string | null;
};

export const ALL_PSAPS_QUERY_KEY = ['configuration', 'all-psaps'] as const;

export async function fetchAllPsaps(): Promise<PsapInfo[]> {
  const { data, response } = await emsApi.GET('/portal/v1/configuration/all-psaps');

  if (data === undefined) {
    throw new Error(`Failed to load PSAP list (HTTP ${response.status})`);
  }

  return data.map((psap) => ({
    id: psap.id ?? '',
    emergencyServiceType: psap.emergencyServiceType ?? 0,
    displayName: psap.displayName ?? null,
  }));
}
